/*
 * Soulog 购物模块（sections/soulog-shop-module.liquid）
 *
 * 四件事：图廊切换、颜色选 variant、数量步进、配件选中 → 合计与加购。
 *
 * 结账有两条路：
 *   1) 没选配件且后台开了「动态结账按钮」—— 用 Shopify 原生的动态结账按钮（Shop Pay / PayPal / Buy it now），
 *      直达结账不经购物车（这条代码改不了去向；2026-10-20 起首页、商品页都应关掉它，统一走 2）。
 *      它长在 section 里的隐藏 product form 中，这里只负责把当前 variant / 数量同步进那个表单。
 *   2) 选了配件（或预售期）—— 原生表单一次只能提交一个 variant，带不上配件，所以换回 data-ssm-buy 按钮：
 *      先 /cart/add.js 加进顾客购物车，再跳购物车页（2026-10-20 起，仿 Plaud；此前直跳结账，见 buyNow）。
 *   3) 只买配件 —— 配件卡右侧的购物车图标（data-ssm-addon-add），只加那一个配件，不带主商品。
 *
 * 加购走主题自己的契约（见 assets/product.js 的 fetch(theme.routes.cart_add_url)）：
 * POST /cart/add.js，成功后派发 cart:refresh 并直接调主题抽屉的 open()（见 openCart）。
 * 这里**不实现任何购物车 UI** —— 那是主题的事，重复实现必然与主题状态打架。
 */
if (!customElements.get("soulog-shop-module")) {
	/*
	 * Storefront API 版本。Shopify 每个版本约支持 12 个月，过期后会静默升到最老的受支持版本
	 * （2026-09 实测 2025-07 被升成 2025-10）。本文件只用 cartLinesAdd（赠品行），升版本风险很低，过期时改这一处即可。
	 */
	const STOREFRONT_API_VERSION = "2026-07";

	/*
	 * 请求超时（2026-10-09）：原先所有请求都不设超时，手机弱网下按钮会一直灰着、没有任何反馈。
	 *   · 写请求（加购 / 赠品行）15s —— 正常 1s 内回，15s 足够覆盖慢 4G，又不至于让人以为卡死；
	 *   · 读顾客购物车（赠品行要拿 cart token）5s —— 读不到就不带，不该拖住购买。
	 * 超时后**不自动重试**：加购超时时服务端可能已经加上了，重试会重复加购（见 postItems）。
	 */
	const WRITE_TIMEOUT_MS = 15000;
	const READ_TIMEOUT_MS = 5000;

	// 顾客能看懂的提示。Shopify 自己的业务错误（库存不足等）原样显示，这里只兜网络层和服务器异常
	const MESSAGES = {
		network: "Network error. Please check your connection and try again.",
		server: "Something went wrong on our end. Please try again in a moment.",
		addUnconfirmed: "We couldn't confirm your cart was updated. Please check your cart before trying again.",
		giftFailed: "Your item was added, but we couldn't add the free gift. Please check your cart.",
		// 立即购买弹出抽屉、价格还在更新时点了钱包 / 改数量的提示（见 installDrawerGuard）
		drawerUpdating: "Updating your cart…",
	};

	/**
	 * fetch + 读 JSON，整段（含读响应体）受 ms 超时约束。
	 * 返回 { ok, status, data }，data 读不出 JSON（如 502 错误页）时为 null —— 这算「服务器回了话」。
	 * 网络层失败原样抛；超时抛 name === "TimeoutError" 的错误。
	 * 浏览器没有 AbortController 时退化成不限时的普通请求。
	 */
	async function fetchJSON(url, init, ms) {
		const controller = typeof AbortController === "function" ? new AbortController() : null;
		let timedOut = false;
		const timer = controller
			? setTimeout(() => {
					timedOut = true;
					controller.abort();
				}, ms)
			: 0;
		try {
			const response = await fetch(url, controller ? { ...init, signal: controller.signal } : init);
			const data = await response.json().catch((error) => {
				if (timedOut) throw error;
				return null;
			});
			return { ok: response.ok, status: response.status, data };
		} catch (error) {
			if (!timedOut) throw error;
			const timeout = new Error("Request timed out");
			timeout.name = "TimeoutError";
			throw timeout;
		} finally {
			if (timer) clearTimeout(timer);
		}
	}

	/** 处理中：禁用防连点 + aria-busy 告诉读屏「正在处理」 */
	function setBusy(buttons, busy) {
		buttons.forEach((b) => {
			b.disabled = busy;
			if (busy) b.setAttribute("aria-busy", "true");
			else b.removeAttribute("aria-busy");
		});
	}

	/**
	 * 把 Section Rendering API 拿到的抽屉 / 角标 HTML 换进页面，规则与主题 assets/app.js 的
	 * CartDrawer.refresh() 一致（#Cart-Drawer 里找 .cart-drawer，找不到就换它自己的 innerHTML）。
	 * 返回 true = 抽屉内容已换成最新。
	 */
	function renderDrawerSections(sections) {
		const targets = [
			{ id: "Cart-Drawer", section: "cart-drawer", selector: ".cart-drawer" },
			{ id: "cart-drawer-toggle", section: "cart-bubble", selector: ".thb-item-count" },
		];
		let drawerDone = false;
		targets.forEach(({ id, section, selector }) => {
			const el = document.getElementById(id);
			const html = sections && sections[section];
			if (!el || typeof html !== "string") return;
			const source = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
			if (!source) return;
			// 整份换成主题渲染出来的节点（不自己拼 HTML）；钱包按钮等自定义元素插进文档时会自己升级
			(el.querySelector(selector) || el).replaceChildren(...source.childNodes);
			if (id === "Cart-Drawer") drawerDone = true;
		});
		return drawerDone;
	}

	/**
	 * 立即购买弹出的抽屉在加购完成前的两种过渡态（样式见 assets/soulog-cart.css 第 3 节）：
	 *   · loading：抽屉原本是空的，显示骨架屏；
	 *   · stale：抽屉原本有商品，先显示上一次的内容（不可点），换好后数字滚动到新值（仿 heypocket）。
	 */
	const DRAWER_LOADING_CLASS = "soulog-drawer-loading";
	const DRAWER_STALE_CLASS = "soulog-drawer-stale";
	/** 先画后对账期间：价格类数字变灰，表示「更新中」 */
	const DRAWER_PREDICT_CLASS = "soulog-drawer-predicting";

	/** 抽屉行里这个 variant 的图（预测换图与提前预载用同一个地址，预载的就是会显示的那张） */
	function drawerImageUrl(variant) {
		const image = variant && variant.featured_image && variant.featured_image.src;
		return image ? `${image}${image.includes("?") ? "&" : "?"}width=240` : "";
	}

	/**
	 * 顾客选了某个颜色 / 卡包就先把抽屉尺寸的那张图下好（2026-10-29）：先画后对账换图时不用再等下载，
	 * 否则新颜色的字先出来、图还是旧颜色。只在手机端（只有手机 Buy Now 弹抽屉），只下选中的那一张（约 10KB）。
	 */
	/**
	 * 抽屉「更新中」（.soulog-drawer-stale，约 1–2s）的点击守卫（2026-10-29 用户：按钮外观保持正常、不变灰）。
	 *
	 * 这段时间加购还在后台进行，真实购物车是半成品（可能只有赠品卡包 / 还是旧颜色），此时去结账会带错商品：
	 *   · Checkout：我们自己的按钮 —— 拦下默认提交、按钮转圈，更新完自动提交（fillDrawerAfterBuyNow），点击不丢；
	 *     不拦事件传播，这次真实点击的 module_click 照常上报，之后的自动提交不再算一次点击；
	 *   · 钱包按钮（Shop Pay / Amazon Pay / PayPal / Google Pay）：Shopify 封闭 shadow DOM + iframe，点击接不住也重放不了，
	 *     CSS 在上面盖一层透明遮罩（点击落在 .additional-checkout-buttons 上），这里吞掉并提示「Updating your cart…」；
	 *   · 商品行 +/− / 删除 / 数量输入：画面还是旧行，放行会改错行 —— 同样吞掉并提示；商品链接、关闭 × 照常。
	 * 挂在 window 捕获阶段：GA4 删除 / 钱包点击埋点与合并行脚本都在 document 上且注册更早，只有 window 先于它们，
	 * 吞掉的点击不会被误报成删除或钱包点击。
	 */
	function installDrawerGuard() {
		if (window.__soulogDrawerGuard) return;
		window.__soulogDrawerGuard = true;
		const staleDrawer = (el) => {
			const drawer = el && el.closest ? el.closest("cart-drawer") : null;
			return drawer && drawer.classList.contains(DRAWER_STALE_CLASS) ? drawer : null;
		};
		const blocked = (el) =>
			el.closest(".soulog-drawer-checkout .additional-checkout-buttons") ||
			(el.closest(".product-cart-item") && el.closest("button, input, select, cart-remove, quantity-selector"));
		["pointerdown", "click", "change", "input"].forEach((type) => {
			window.addEventListener(
				type,
				(event) => {
					const target = event.target;
					const drawer = staleDrawer(target);
					if (!drawer) return;
					if (type === "click" && target.closest(".soulog-drawer-checkout .checkout-button")) {
						event.preventDefault();
						const button = target.closest(".checkout-button");
						button.classList.add("loading");
						button.setAttribute("aria-busy", "true");
						drawer.__soulogPendingCheckout = true;
						return;
					}
					if (!blocked(target)) return;
					event.preventDefault();
					event.stopPropagation();
					if (type === "click") showDrawerUpdating(drawer);
				},
				true,
			);
		});
	}

	/** 「Updating your cart…」小提示：挂在 <cart-drawer> 上，抽屉内容换掉（更新完）时随之消失 */
	function showDrawerUpdating(drawer) {
		if (drawer.querySelector(".soulog-drawer-updating")) return;
		const note = document.createElement("p");
		note.className = "soulog-drawer-updating";
		note.setAttribute("role", "status");
		note.textContent = MESSAGES.drawerUpdating;
		drawer.append(note);
	}

	/** 更新完：Checkout 若在更新中被点过，用新渲染的按钮提交（submitter 带上 name=checkout，购买路径照常注入） */
	function resumeDrawerCheckout(drawer) {
		const note = drawer.querySelector(".soulog-drawer-updating");
		if (note) note.remove();
		if (!drawer.__soulogPendingCheckout) return;
		drawer.__soulogPendingCheckout = false;
		const button = drawer.querySelector(".soulog-drawer-checkout .checkout-button");
		const form = button && button.form;
		if (!form) return;
		if (typeof form.requestSubmit === "function") form.requestSubmit(button);
		else button.click();
	}

	/** 没加上购物车：在更新中点过的 Checkout 作废（抽屉收起，错误提示在购物模块里） */
	function cancelDrawerCheckout(drawer) {
		drawer.__soulogPendingCheckout = false;
		const note = drawer.querySelector(".soulog-drawer-updating");
		if (note) note.remove();
		drawer.querySelectorAll(".checkout-button.loading").forEach((b) => {
			b.classList.remove("loading");
			b.removeAttribute("aria-busy");
		});
	}

	const preloadedDrawerImages = new Set();
	function preloadDrawerImage(variant) {
		const url = drawerImageUrl(variant);
		if (!url || preloadedDrawerImages.has(url)) return;
		if (!window.matchMedia || !window.matchMedia("(max-width: 767px)").matches) return;
		preloadedDrawerImages.add(url);
		const img = new Image();
		img.decoding = "async";
		img.src = url;
	}

	/** 把抽屉里一行改成指定 variant 的图 / 规格 / 数量（只动显示，服务端结果回来会整块换掉） */
	function paintDrawerRow(row, variant, qty) {
		const url = drawerImageUrl(variant);
		if (url) {
			row.querySelectorAll(".product-cart-item-image img").forEach((img) => {
				img.removeAttribute("srcset");
				img.src = url;
			});
		}
		const values = variant.options || [];
		// 手机端写法「Color: Titanium」：沿用行里原有的选项名，只换值
		row.querySelectorAll(".product-cart-item-options.soulog-drawer-mobile").forEach((el) => {
			const names = [...el.querySelectorAll("span")].map((n) => n.textContent);
			if (names.length !== values.length) return;
			el.textContent = "";
			names.forEach((name, i) => {
				if (i) el.append(", ");
				const label = document.createElement("span");
				label.textContent = name;
				el.append(label, ` ${values[i]}`);
			});
		});
		// 桌面写法只有值「Titanium」
		row.querySelectorAll(".product-cart-item-options.soulog-drawer-desktop").forEach((el) => {
			el.textContent = values.join(", ");
		});
		row.querySelectorAll("input.qty").forEach((input) => {
			input.value = String(qty);
		});
	}

	/** 换内容后要「滚动」的数字：总价 / 划线原价 / 省多少 / 标题旁件数（都只有一个，按选择器一一对应新旧节点） */
	const ROLLING_NUMBERS = [
		".soulog-drawer-summary__amount",
		".soulog-drawer-summary__was",
		".soulog-drawer-summary__save",
		".side-panel-header sup",
	];
	const NUMBER_IN_TEXT = /\d[\d,]*(?:\.\d+)?/;
	const ROLL_MS = 600;

	function numberIn(text) {
		const m = NUMBER_IN_TEXT.exec(text || "");
		return m ? Number(m[0].replace(/,/g, "")) : null;
	}

	/** 记下抽屉里这几个数字当前的值（没有的项记 null） */
	function readDrawerNumbers(drawer) {
		return ROLLING_NUMBERS.map((sel) => {
			const el = drawer.querySelector(sel);
			return el ? numberIn(el.textContent) : null;
		});
	}

	/**
	 * 换好内容后，把变了的数字从旧值滚到新值（约 0.6s，先快后慢）。只改文字里的数字部分，
	 * 货币符号 / 「(Save …)」等原样保留；结束时恢复成服务端渲染的原文，不留格式误差。
	 * 旧值不存在（原本是空购物车）或系统要求减少动效时不滚，直接显示新值。
	 */
	function rollDrawerNumbers(drawer, before) {
		if (typeof requestAnimationFrame !== "function") return;
		if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		ROLLING_NUMBERS.forEach((sel, i) => {
			const from = before[i];
			const el = drawer.querySelector(sel);
			if (from == null || !el) return;
			const finalText = el.textContent;
			const match = NUMBER_IN_TEXT.exec(finalText);
			if (!match) return;
			const to = Number(match[0].replace(/,/g, ""));
			if (to === from) return;
			const decimals = (match[0].split(".")[1] || "").length;
			const format = (n) =>
				n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
			const start = performance.now();
			const step = (now) => {
				const t = Math.min(1, (now - start) / ROLL_MS);
				const eased = 1 - Math.pow(1 - t, 3);
				if (t >= 1) {
					el.textContent = finalText;
					return;
				}
				el.textContent = finalText.replace(match[0], format(from + (to - from) * eased));
				requestAnimationFrame(step);
			};
			el.textContent = finalText.replace(match[0], format(from));
			requestAnimationFrame(step);
			// 兜底：标签页切到后台时 rAF 会停，数字不能停在旧值
			setTimeout(() => {
				el.textContent = finalText;
			}, ROLL_MS + 200);
		});
	}

	/** 收起主题抽屉：走主题的 OverlayManager（会一并撤掉遮罩），拿不到就点它自己的关闭按钮 */
	function closeDrawer(drawer) {
		drawer.classList.remove(DRAWER_LOADING_CLASS, DRAWER_STALE_CLASS, DRAWER_PREDICT_CLASS);
		if (typeof OverlayManager !== "undefined" && OverlayManager && typeof OverlayManager.close === "function") {
			OverlayManager.close(drawer);
			return;
		}
		const close = drawer.querySelector("side-panel-close");
		if (close) close.click();
	}

	class SoulogShopModule extends HTMLElement {
		connectedCallback() {
			this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
			this.variants = this.parseJSON(this.dataset.variants, []);
			this.addonVariants = this.parseJSON(this.dataset.addonVariants, []);
			// 赠品模式：配件免费由结账时的自动折扣完成，Total 不计配件价（2026-09-27）
			this.addonIsGift = this.dataset.addonGift === "true";
			// 自动折扣展示（后台自动订单折扣的镜像，2026-09-30）：百分比 + 最低消费（分）
			this.discountPercent = Number(this.dataset.autoDiscountPercent) || 0;
			this.discountMinCents = Number(this.dataset.autoDiscountMin) || 0;

			// 颜色在 variant.options 里的下标（Liquid 按颜色选项名算好；没有颜色选项为 -1）
			const colorIndex = Number(this.dataset.colorIndex);
			this.colorIndex = Number.isInteger(colorIndex) ? colorIndex : -1;

			this.currentVariantId = Number(this.dataset.selectedVariant) || (this.variants[0] && this.variants[0].id);
			// 套餐变体模式下 currentVariantId 只指「主机单卖」变体（链接带 ?variant=套餐 时也归一回来）
			this.currentVariantId = this.deviceVariantFor(this.currentVariant()).id || this.currentVariantId;
			this.qty = 1;
			this.slideIndex = 0;
			// 本次切图是怎么来的，renderGallery 交给 noteImage 记 gallery_image_view（见「图廊埋点」）
			this.navTrigger = "initial";

			// 卖点首图先转成真正的第一张、其余删掉，再收集图廊 —— 顺序反了索引会错位
			this.applySellingPoint();

			this.slides = Array.from(this.querySelectorAll("[data-ssm-slide]"));
			this.dots = Array.from(this.querySelectorAll("[data-ssm-dot]"));
			this.thumbs = Array.from(this.querySelectorAll("[data-ssm-thumb]"));
			this.setupPanes();
			this.prev = this.querySelector("[data-ssm-prev]");
			this.next = this.querySelector("[data-ssm-next]");
			this.qtyInput = this.querySelector("[data-ssm-qty-input]");
			this.totalNode = this.querySelector("[data-ssm-total]");
			this.totalCompareNode = this.querySelector("[data-ssm-total-compare]");
			// 标题下的单价行（2026-10-20 改版）：一台的折后价 + 划线原价，与合计同一套折扣算法
			this.priceNode = this.querySelector("[data-ssm-price]");
			this.priceCompareNode = this.querySelector("[data-ssm-price-compare]");
			this.colorName = this.querySelector("[data-ssm-color-name]");
			this.addonName = this.querySelector("[data-ssm-addon-name]");
			this.addonNoneLabel = this.addonName ? this.addonName.dataset.noneLabel || "" : "";
			// 配件初值：Liquid 按后台「默认选中第一款配件」直出 aria-pressed（2026-09-30），这里只读不定
			const pressed = this.querySelector('[data-ssm-addon][aria-pressed="true"]');
			this.selectedAddonId = pressed ? Number(pressed.dataset.variantId) : null;
			// 默认已选了卡包时，换成与默认颜色同色的那款（同色套装优先，2026-10-21）；没预选就不替顾客选
			if (this.selectedAddonId) this.matchAddonToColor(this.querySelector("[data-ssm-swatch].is-active"));
			this.errorNode = this.querySelector("[data-ssm-error]");

			// 两列：右列比左列高时让图廊粘住（见 syncGalleryStick）
			this.gallery = this.querySelector(".ssm__gallery");
			this.panel = this.querySelector(".ssm__panel");

			// 原生动态结账按钮所在的表单 + 分期说明表单，都要跟着颜色 / 数量走
			this.dynamicForm = this.querySelector(".ssm__dynamic");
			this.buyButton = this.querySelector("[data-ssm-buy]");
			this.formVariantInputs = Array.from(this.querySelectorAll("[data-ssm-form-variant]"));
			this.formQtyInputs = Array.from(this.querySelectorAll("[data-ssm-form-qty]"));
			// 后台关掉动态结账按钮时服务端就不输出它，此时 data-ssm-buy 常驻，不做切换
			this.hasDynamic = !!(this.dynamicForm && this.dynamicForm.querySelector(".shopify-payment-button"));
			// 按颜色判缺货要改写的按钮；原文案（Buy Now / Pre-order Now）记下来，换回有货色时还原
			this.soldOutLabel = this.dataset.soldOutLabel || "Sold out";
			this.floatBuy = this.querySelector("[data-ssm-float-buy]");
			[this.buyButton, this.floatBuy].forEach((b) => {
				if (b && !("ssmLabel" in b.dataset)) b.dataset.ssmLabel = b.textContent.trim();
			});

			this.bind();
			this.renderGallery();
			this.navTrigger = "other";
			this.bindImageTracking();
			this.renderTotal();
			this.syncForms();
			this.updateBuyMode();
			this.syncGalleryStick();
			this.observeGalleryStick();

			/*
			 * 立即购买成功后按钮保持禁用直到跳走（防二次建单）。顾客在结账页点后退时，
			 * Safari / Chrome 会从 bfcache 原样恢复本页 —— 按钮还灰着、点不动。恢复时解除。
			 */
			this.onPageShow = (event) => {
				if (!event.persisted) return;
				this.leaving = false;
				setBusy(this.querySelectorAll("[data-ssm-buy], [data-ssm-add-to-cart]"), false);
				// 从购物车页后退回来：旧快照不含刚才加的，重读（见 prefetchCart）
				this.prefetchCart();
			};
			window.addEventListener("pageshow", this.onPageShow);

			/*
			 * 预读购物车（见 prefetchCart）：页面空闲时读一次；之后购物车可能变了就重读 ——
			 * 加购 / 抽屉改动（cart:refresh）、从别的标签页切回来（可能在那边改了购物车）。
			 */
			this.onCartChange = () => this.prefetchCart();
			document.documentElement.addEventListener("cart:refresh", this.onCartChange);
			this.onCartVisibility = () => {
				if (document.visibilityState === "visible") this.prefetchCart();
			};
			document.addEventListener("visibilitychange", this.onCartVisibility);
			if (typeof requestIdleCallback === "function") requestIdleCallback(this.onCartChange, { timeout: 4000 });
			else setTimeout(this.onCartChange, 2000);
		}

		/**
		 * 左列图廊粘住 —— 只在右列确实比图廊高时才粘。
		 *
		 * 无条件粘会把图廊按到顶部、底下空出一大片（右列比它矮的时候）。
		 * 40px 容差防止两列高度接近时在临界点反复横跳。
		 *
		 * 触发点：初始化 + 两列尺寸变化（视口、图片加载完）。
		 * 原先还挂在描述折叠的 measure / 展开收起上，那套功能 2026-09-16 按业务要求
		 * 整体回退（预售期本模块整块不显示、副标题回到手填），改由 ResizeObserver 承担。
		 */
		syncGalleryStick() {
			if (!this.gallery || !this.panel) return;
			const slack = this.panel.offsetHeight - this.gallery.offsetHeight;
			this.classList.toggle("is-gallery-sticky", slack > 40);
		}

		observeGalleryStick() {
			if (typeof ResizeObserver !== "function" || !this.gallery || !this.panel) return;
			let raf = 0;
			const ro = new ResizeObserver(() => {
				cancelAnimationFrame(raf);
				raf = requestAnimationFrame(() => this.syncGalleryStick());
			});
			ro.observe(this.gallery);
			ro.observe(this.panel);
			this.stickObserver = ro;
		}

		disconnectedCallback() {
			if (this.stickObserver) this.stickObserver.disconnect();
			if (this.autoplayObserver) this.autoplayObserver.disconnect();
			cancelAnimationFrame(this.progressRaf);
			if (this.progressOff) this.progressOff();
			if (this.onPageShow) window.removeEventListener("pageshow", this.onPageShow);
			if (this.onCartChange) document.documentElement.removeEventListener("cart:refresh", this.onCartChange);
			if (this.onCartVisibility) document.removeEventListener("visibilitychange", this.onCartVisibility);
			if (this.imageObserver) this.imageObserver.disconnect();
			if (this.onImageVisibility) document.removeEventListener("visibilitychange", this.onImageVisibility);
			clearTimeout(this.imageTimer);
		}

		parseJSON(raw, fallback) {
			try {
				return JSON.parse(raw) || fallback;
			} catch {
				return fallback;
			}
		}

		bind() {
			if (this.prev) this.prev.addEventListener("click", () => this.nav("prev", () => this.go(this.slideIndex - 1)));
			if (this.next) this.next.addEventListener("click", () => this.nav("next", () => this.go(this.slideIndex + 1)));
			// 圆点 / 缩略图按「当前分组」里的位置跳（分组切换后 this.dots / this.thumbs 只含当前组）
			this.allDots.forEach((d) =>
				d.addEventListener("click", () => this.nav("dot", () => this.go(this.dots.indexOf(d)))),
			);
			this.allThumbs.forEach((t) =>
				t.addEventListener("click", () => this.nav("thumb", () => this.go(this.thumbs.indexOf(t)))),
			);
			this.tabs.forEach((t) =>
				t.addEventListener("click", () => this.nav("tab", () => this.setPane(t.dataset.ssmTab))),
			);
			this.bindVideoToggles();
			this.bindSwipe();
			this.bindAutoplay();

			this.querySelectorAll("[data-ssm-swatch]").forEach((el) => {
				el.addEventListener("click", () => this.nav("color", () => this.selectColor(el)));
			});

			this.querySelectorAll("[data-ssm-qty]").forEach((el) => {
				el.addEventListener("click", () => this.stepQty(Number(el.dataset.ssmQty)));
			});
			if (this.qtyInput) {
				this.qtyInput.addEventListener("change", () => {
					this.qty = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
					this.qtyInput.value = this.qty;
					this.renderTotal();
					this.syncForms();
				});
			}

			this.querySelectorAll("[data-ssm-addon]").forEach((el) => {
				el.addEventListener("click", () => this.nav("addon", () => this.toggleAddon(el)));
			});

			// 卡片下方的卡包缩略图（Figma 1731:3342）：点了直接换成那款卡包
			this.querySelectorAll("[data-ssm-addon-thumb]").forEach((el) => {
				el.addEventListener("click", () => this.nav("addon", () => this.pickAddonThumb(el)));
			});

			// 「More Colors / Show Less」：展开 / 收起配件卡（Figma 1531:3626）
			const more = this.querySelector("[data-ssm-addons-more]");
			if (more) {
				more.addEventListener("click", () => {
					const list = this.querySelector(".ssm__addons");
					const open = !(list && list.classList.contains("is-expanded"));
					if (list) list.classList.toggle("is-expanded", open);
					more.setAttribute("aria-expanded", open ? "true" : "false");
					// 收起时列表变短，按钮可能被顶出屏幕：拉回视野
					if (!open) more.scrollIntoView({ block: "nearest" });
				});
			}

			// 配件卡右侧的购物车图标：只买这一件，不带硬件
			this.querySelectorAll("[data-ssm-addon-add]").forEach((el) => {
				el.addEventListener("click", () => this.addAddonOnly(el));
			});

			const buy = this.querySelector("[data-ssm-buy]");
			const add = this.querySelector("[data-ssm-add-to-cart]");
			if (buy) buy.addEventListener("click", () => this.submit(true));
			if (add) add.addEventListener("click", () => this.submit(false));
		}

		/* ---------------------------------------------------------- 图廊 */

		/**
		 * 图廊左右划切图（2026-10-12）。箭头 / 圆点 / 缩略图之外的第四个入口，手机上最常用。
		 *
		 * 图是叠放淡入淡出，不是横向轨道，所以不跟手拖，只在松手时判方向切一张。
		 * CSS 给 .ssm__slides 设了 touch-action: pan-y：竖向滚页面归浏览器（它接管时会发
		 * pointercancel，这里就作废这次手势），横向手势交给这里。
		 * 鼠标也算：Shopify 编辑器的手机预览是「窄视口 + 鼠标」。
		 */
		bindSwipe() {
			const area = this.querySelector("[data-ssm-slides]");
			if (!area || this.slides.length < 2) return;
			const MIN_DX = 40;
			let start = null;
			let swallowClick = false;

			area.addEventListener("pointerdown", (e) => {
				if (e.pointerType === "mouse" && e.button !== 0) return;
				start = { id: e.pointerId, x: e.clientX, y: e.clientY };
				swallowClick = false;
			});
			area.addEventListener("pointerup", (e) => {
				if (!start || e.pointerId !== start.id) return;
				const dx = e.clientX - start.x;
				const dy = e.clientY - start.y;
				start = null;
				// 横向位移要够长，且明显大于竖向（斜着划多半是在滚页面）
				if (Math.abs(dx) < MIN_DX || Math.abs(dx) < Math.abs(dy) * 1.5) return;
				swallowClick = true;
				this.nav("swipe", () => this.go(this.slideIndex + (dx < 0 ? 1 : -1)));
			});
			area.addEventListener("pointercancel", () => {
				start = null;
			});
			// 鼠标划完松手还会补一个 click，吞掉以免误触图上的链接
			area.addEventListener(
				"click",
				(e) => {
					if (!swallowClick) return;
					swallowClick = false;
					e.preventDefault();
					e.stopPropagation();
				},
				true,
			);
			// 不拦的话鼠标一拖就成了浏览器原生「拖图片」，pointerup 收不到
			area.addEventListener("dragstart", (e) => e.preventDefault());
		}

		/**
		 * 图廊 4 秒自动轮播（2026-10-21，Figma 1551:4886；同日用户要求桌面也与手机一致）。
		 *
		 * 计时交给 CSS：当前那段进度条的 ::after 用 4 秒动画从左填满（ssm-dot-fill），
		 * 这里只在动画结束时切下一张（最后一张回到第一张）。进度条和切图因此永远同步，
		 * 手动切图 / 切组时新的那段重新挂上 is-active，动画自然从头开始。
		 * 「减少动态效果」下 CSS 不挂动画，animationend 不会来，也就不轮播。
		 * 模块滚出屏幕时暂停动画（is-autoplay-paused），标签页切到后台浏览器本身就不跑动画。
		 */
		bindAutoplay() {
			const dots = this.querySelector("[data-ssm-dots]");
			if (!dots || !this.stage) return;
			dots.addEventListener("animationend", (e) => {
				if (e.animationName !== "ssm-dot-fill" || !e.target.classList.contains("is-active")) return;
				if (this.slides.length < 2) return;
				this.nav("auto", () => this.go((this.slideIndex + 1) % this.slides.length));
			});
			if (typeof IntersectionObserver !== "function") return;
			const io = new IntersectionObserver((entries) => {
				entries.forEach((en) => this.stage.classList.toggle("is-autoplay-paused", !en.isIntersecting));
			});
			io.observe(this.stage);
			this.autoplayObserver = io;
		}

		/**
		 * 视频那张的进度段跟视频走（2026-10-21 用户）：不跑 4 秒动画（CSS 见 .ssm__dot.is-video），
		 * 而是按 currentTime / duration 填白（rAF 刷新，暂停时停在原处）；视频循环跳回开头 = 播完一轮，
		 * 自动轮播开着时切下一张。
		 * 跳回判定放在 timeupdate 里，不放 rAF：layout/theme.liquid 的 video_complete 也是在 timeupdate 里认
		 * 「上一刻在结尾、这一刻回到前面」，两边吃同一次事件，这边切走暂停不会吞掉那条完播。
		 */
		syncVideoProgress() {
			cancelAnimationFrame(this.progressRaf);
			if (this.progressOff) this.progressOff();
			this.progressOff = null;
			const slide = this.slides[this.slideIndex];
			const dot = this.dots[this.slideIndex];
			const video = slide && slide.querySelectorAll("video")[0];
			this.allDots.forEach((d) => d.classList.toggle("is-video", !!video && d === dot));
			if (!video || !dot) return;
			// Loop only while the video is actually playing: paused / scrolled away (IO pauses it) it used to keep
			// running every frame for nothing — on phones that is battery (2026-10-23)
			const paint = () => {
				const d = video.duration;
				if (d > 0) dot.style.setProperty("--ssm-progress", String(Math.min(video.currentTime / d, 1)));
				this.progressRaf = video.paused ? 0 : requestAnimationFrame(paint);
			};
			// cancel first so a seek while playing doesn't start a second loop
			const repaint = () => {
				cancelAnimationFrame(this.progressRaf);
				paint();
			};
			paint();
			let last = 0;
			const onTime = () => {
				const now = video.currentTime;
				const looped = video.duration > 0 && last >= video.duration - 1 && now < last;
				last = now;
				if (looped && this.autoplayOn() && this.slides.length > 1)
					this.nav("auto", () => this.go((this.slideIndex + 1) % this.slides.length));
			};
			video.addEventListener("timeupdate", onTime);
			// play restarts the loop; pause / seek repaint once so the bar shows where it stopped
			const events = ["play", "pause", "seeked"];
			events.forEach((type) => video.addEventListener(type, repaint));
			this.progressOff = () => {
				video.removeEventListener("timeupdate", onTime);
				events.forEach((type) => video.removeEventListener(type, repaint));
			};
		}

		/** 与 CSS 挂进度动画的条件一致：没开「减少动态效果」（2026-10-21 起桌面也同手机轮播） */
		autoplayOn() {
			return !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
		}

		/* ------------------------------------------------------ 卖点首屏 */

		/**
		 * 按广告卖点换首屏（2026-10-15）。判定已由 section 开头的内联脚本完成，结果在 <html data-soulog-sp>，
		 * 首帧靠 CSS 显示。这里只做收尾：命中的卖点图 / 缩略图 / 圆点转成图廊的第一项（箭头、划动、切色照常工作），
		 * 没命中的删掉；短句不用动，一直按 CSS 显示。同一代码只留第一个。
		 */
		applySellingPoint() {
			const code = document.documentElement.getAttribute("data-soulog-sp") || "";
			const promote = (attr, real) => {
				let kept = false;
				this.querySelectorAll(`[${attr}]`).forEach((el) => {
					if (!kept && code && el.getAttribute(attr) === code) {
						el.removeAttribute(attr);
						el.setAttribute(real, "");
						kept = true;
					} else {
						el.remove();
					}
				});
			};
			promote("data-ssm-sp", "data-ssm-slide");
			promote("data-ssm-sp-thumb", "data-ssm-thumb");
			promote("data-ssm-sp-dot", "data-ssm-dot");
			this.reportSellingPoint(code);
		}

		/**
		 * GA4 selling_point_assign：只在本次有卖点来源（广告带了卖点，或上次广告点击存下的）时发，每次页面浏览一条。
		 * selling_point = 实际显示的版本（没配内容时 default），selling_points = 广告里列的全部卖点。
		 * 编辑器预览不报。字典：docs/ga4-埋点字典.md。
		 */
		reportSellingPoint(code) {
			if (window.__soulogSpReported || (window.Shopify && window.Shopify.designMode)) return;
			const sp = window.__soulogSellingPoint;
			const cur = sp ? sp.current() : null;
			if (!cur || !cur.codes.length) return;
			window.__soulogSpReported = true;
			const params = {
				selling_point: code || "default",
				selling_points: cur.codes.join("-"),
				sp_source: cur.source,
			};
			const fire = () => {
				if (typeof window.__soulogGa4Event === "function")
					window.__soulogGa4Event("selling_point_assign", params, this);
			};
			if (typeof window.__soulogGa4Event === "function") fire();
			else document.addEventListener("DOMContentLoaded", fire, { once: true });
		}

		/**
		 * 图廊分组（2026-10-20 改版，Figma 1510:1729）：Products / Scenarios / Video / Pricing。
		 * 所有分组的 slide / 圆点 / 缩略图都在同一套容器里，靠 data-ssm-pane 区分（不带的就是 products）。
		 * this.slides / dots / thumbs 永远只含当前组，箭头、圆点、滑动、缩略图因此天然只在组内走。
		 */
		setupPanes() {
			this.allSlides = this.slides;
			this.allDots = this.dots;
			this.allThumbs = this.thumbs;
			this.tabs = Array.from(this.querySelectorAll("[data-ssm-tab]"));
			this.stage = this.querySelector(".ssm__stage");
			this.setupOptionMedia();
			this.pane = "";
			this.setPane("products");
		}

		/**
		 * 颜色图 / 卡包图默认不进 Products 组（2026-10-21 用户）：点了颜色 / 卡包才出现（showOptionMedia）。
		 * 颜色图 = 任一 variant 的 featured_media；卡包图 = alt 对得上配件卡标题的那张（与选配件跳图同一套匹配）。
		 * 商品图的 slide / 圆点 / 缩略图在 DOM 里同序，按序号成组，隐藏和挪位都三个一起动。
		 */
		setupOptionMedia() {
			const own = (list) => list.filter((el) => !el.dataset.ssmPane);
			const dots = own(this.allDots);
			const thumbs = own(this.allThumbs);
			this.optionGroups = new Map();
			own(this.allSlides).forEach((slide, i) => {
				this.optionGroups.set(slide, [slide, dots[i], thumbs[i]].filter(Boolean));
			});
			const productSlides = Array.from(this.optionGroups.keys());
			const mediaIds = new Set(
				(this.variants || []).map((v) => v && v.featured_media && v.featured_media.id).filter(Boolean),
			);
			const hidden = productSlides.filter((s) => mediaIds.has(Number(s.dataset.mediaId)));
			this.querySelectorAll("[data-ssm-addon]").forEach((card) => {
				const idx = this.findSlideByTitle(card.dataset.title, productSlides);
				if (idx > -1) hidden.push(productSlides[idx]);
			});
			this.optionHidden = new Set();
			hidden.forEach((s) => this.optionGroups.get(s).forEach((el) => this.optionHidden.add(el)));
			this.optionPins = { color: null, addon: null };
			this.optionShown = new Set();
		}

		/**
		 * 点颜色 / 卡包时露出它的图：放到 Products 组第一张并直接显示；同一类只露一张（换色直接替换）。
		 * slide 传 null = 收起这一类（取消卡包，或新颜色没有图）。
		 */
		showOptionMedia(kind, slide) {
			if (!this.optionPins) return;
			const group = slide && this.optionGroups.get(slide);
			this.optionPins[kind] = group && this.optionHidden.has(slide) ? slide : null;
			this.optionShown = new Set();
			Object.values(this.optionPins).forEach(
				(s) => s && this.optionGroups.get(s).forEach((el) => this.optionShown.add(el)),
			);
			const pinned = this.optionPins[kind];
			if (pinned) {
				const moved = new Set(this.optionGroups.get(pinned));
				const toFront = (list) => [...list.filter((el) => moved.has(el)), ...list.filter((el) => !moved.has(el))];
				this.allSlides = toFront(this.allSlides);
				this.allDots = toFront(this.allDots);
				this.allThumbs = toFront(this.allThumbs);
				// 圆点 / 缩略图按 DOM 顺序排，挪到各自容器最前（商品组本来就在最前）
				moved.forEach((el) => {
					const parent = el.parentElement;
					if (parent && parent.firstElementChild !== el) parent.insertBefore(el, parent.firstElementChild);
				});
			}
			this.pane = "";
			this.setPane("products");
			if (pinned) this.go(this.slides.indexOf(pinned));
		}

		paneOf(el) {
			return el.dataset.ssmPane || "products";
		}

		setPane(name) {
			if (!this.allSlides || name === this.pane) return;
			this.pane = name;
			const inPane = (el) =>
				this.paneOf(el) === name && (!this.optionHidden || !this.optionHidden.has(el) || this.optionShown.has(el));
			this.slides = this.allSlides.filter(inPane);
			this.dots = this.allDots.filter(inPane);
			this.thumbs = this.allThumbs.filter(inPane);
			this.allSlides.forEach((s) => s.classList.remove("is-active"));
			[...this.allDots, ...this.allThumbs].forEach((el) => {
				el.classList.toggle("is-pane-on", inPane(el));
				el.classList.toggle("is-pane-off", !inPane(el));
			});
			this.tabs.forEach((t) => {
				const on = t.dataset.ssmTab === name;
				t.classList.toggle("is-active", on);
				t.setAttribute("aria-selected", on ? "true" : "false");
			});
			// 只有一张（视频 / 价格图）时箭头和圆点没有意义
			if (this.stage) this.stage.classList.toggle("is-single", this.slides.length < 2);
			this.slideIndex = 0;
			this.renderGallery();
		}

		/**
		 * 视频右上角的播放 / 暂停（2026-10-20）。图标与 aria-label 跟着 video 的 play / pause 事件走，
		 * 所以切组时脚本自动播放 / 暂停也会同步，不会出现「在播却显示播放键」。
		 */
		bindVideoToggles() {
			this.querySelectorAll("[data-ssm-video-toggle]").forEach((btn) => {
				const slide = btn.closest("[data-ssm-slide]");
				const video = slide && slide.querySelector("video");
				if (!video) return;
				const sync = () => {
					slide.classList.toggle("is-video-paused", video.paused);
					btn.setAttribute("aria-label", video.paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
				};
				video.addEventListener("play", sync);
				video.addEventListener("pause", sync);
				sync();
				btn.addEventListener("click", (event) => {
					event.stopPropagation();
					if (video.paused) {
						const playing = video.play();
						if (playing && playing.catch) playing.catch(() => {});
					} else {
						video.pause();
					}
				});
			});
		}

		go(index) {
			if (!this.slides.length) return;
			const max = this.slides.length - 1;
			this.slideIndex = Math.min(Math.max(index, 0), max);
			this.renderGallery();
		}

		renderGallery() {
			/*
			 * is-near = rendered but not shown (CSS hides the rest, so their lazy images don't download, 2026-10-23):
			 * the neighbours on both sides (wrap-around, autoplay loops) plus the slide we just left, so it can fade out.
			 */
			const leaving = (this.allSlides || this.slides).filter((s) => s.classList.contains("is-active"));
			const n = this.slides.length;
			const near = new Set(leaving);
			if (n > 1) {
				near.add(this.slides[(this.slideIndex + 1) % n]);
				near.add(this.slides[(this.slideIndex - 1 + n) % n]);
			}
			(this.allSlides || this.slides).forEach((s) => s.classList.toggle("is-near", near.has(s)));
			this.slides.forEach((s, i) => s.classList.toggle("is-active", i === this.slideIndex));
			/*
			 * 视频（autoplay muted loop）：切到它时主动 play —— autoplay 属性只在加载时生效一次，
			 * 而页面初始在 Products 组时这里已经把它暂停了，之后切过来不会自己再播（2026-10-20 用户反馈）。
			 * 切走的视频暂停。浏览器只放行静音自动播放，所以先确保 muted。
			 */
			(this.allSlides || this.slides).forEach((s) => {
				const on = s.classList.contains("is-active");
				s.querySelectorAll("video").forEach((v) => {
					if (!on) return v.pause();
					v.muted = true;
					const playing = v.play && v.play();
					if (playing && playing.catch) playing.catch(() => {});
				});
			});
			this.dots.forEach((d, i) => d.classList.toggle("is-active", i === this.slideIndex));
			this.thumbs.forEach((t, i) => t.classList.toggle("is-active", i === this.slideIndex));
			if (this.prev) this.prev.disabled = this.slideIndex === 0;
			if (this.next) this.next.disabled = this.slideIndex >= this.slides.length - 1;
			this.revealThumb(this.thumbs[this.slideIndex]);
			this.syncVideoProgress();
			this.noteImage(this.slides[this.slideIndex], this.navTrigger);
		}

		/* ------------------------------------------------------ 图廊埋点 */

		/** 带着「怎么切过来的」执行一次切图（箭头 / 圆点 / 滑动 / 自动轮播 …），切完恢复默认 other */
		nav(trigger, fn) {
			this.navTrigger = trigger;
			try {
				fn();
			} finally {
				this.navTrigger = "other";
			}
		}

		/**
		 * GA4 gallery_image_view（2026-10-26，用户：想知道哪张图最吸引人、看到哪张才买）。
		 * 一张图成为当前图、图廊露出 ≥ 一半、页面在前台，连续满 1 秒记一条。
		 * 同一张图每次页面浏览最多两条：被动（进页默认 initial / 自动轮播 auto）一条、主动（箭头 / 圆点 /
		 * 缩略图 / 滑动 / 切组 / 选颜色 / 选卡包）一条 —— 自动轮播 4 秒一张，不分开会把「被轮播带过」算成「看过」。
		 * 主动切到的最后一张另记为 gallery_picked，与当前图 gallery_image 一起挂到模块里的点击上
		 * （购买按钮 / 钱包按钮也在内，见 assets/soulog-module-tracking.js 的 galleryContext）。
		 * 编辑器预览不报。字典：docs/ga4-埋点字典.md。
		 */
		bindImageTracking() {
			this.seenImages = new Set();
			this.stageVisible = false;
			this.setAttribute("data-ga4-gallery", "");
			if (!this.stage || (window.Shopify && window.Shopify.designMode)) return;
			if (typeof IntersectionObserver === "function") {
				const io = new IntersectionObserver(
					(entries) => {
						entries.forEach((en) => {
							this.stageVisible = en.isIntersecting && en.intersectionRatio >= 0.5;
						});
						this.armImageView();
					},
					{ threshold: [0, 0.5, 1] },
				);
				io.observe(this.stage);
				this.imageObserver = io;
			}
			this.onImageVisibility = () => this.armImageView();
			document.addEventListener("visibilitychange", this.onImageVisibility);
			// 初次 renderGallery 在本方法之前，补记进页默认的那张
			this.noteImage(this.slides[this.slideIndex], "initial");
		}

		/** 图片在 GA4 里的身份：分组 / 组内第几张 / 描述 / 文件名（颜色图、卖点图会让序号变，跨天对比认文件名） */
		imageInfo(slide) {
			if (!slide) return null;
			const img = slide.querySelector("img");
			const video = slide.querySelector("video");
			const src =
				(img && (img.getAttribute("src") || img.currentSrc)) ||
				(video && (video.getAttribute("src") || (video.querySelector("source") || {}).src || video.poster)) ||
				"";
			const file = String(src).split("?")[0].split("/").pop() || "";
			const name = slide.dataset.mediaAlt || (img && img.getAttribute("alt")) || slide.dataset.ga4Video || file;
			return {
				slide,
				group: this.paneOf(slide),
				position: this.slides.indexOf(slide) + 1,
				name: String(name).trim().slice(0, 100),
				file: file.slice(0, 100),
			};
		}

		noteImage(slide, trigger) {
			if (!this.seenImages || !slide) return;
			const active = trigger !== "initial" && trigger !== "auto";
			const cur = this.curImage;
			if (cur && cur.slide === slide) {
				// 同一张又被主动点中（如点当前圆点）：升级成主动，重新计时
				if (!active || cur.active) return;
			}
			const info = this.imageInfo(slide);
			this.curImage = Object.assign(info, { trigger, active });
			if (active) this.pickedImage = info;
			this.armImageView();
		}

		armImageView() {
			clearTimeout(this.imageTimer);
			const cur = this.curImage;
			if (!cur || !this.stageVisible || document.visibilityState === "hidden") return;
			const key = `${cur.group}|${cur.file || cur.name}|${cur.active ? "a" : "p"}`;
			if (this.seenImages.has(key)) return;
			this.imageTimer = setTimeout(() => {
				if (this.curImage !== cur || typeof window.__soulogGa4Event !== "function") return;
				this.seenImages.add(key);
				// 主动看过的图也记进订单摘要 _soulog_journey.gallery（结账从购物车页发起，那里没有图廊）
				const journey = window.__soulogJourney;
				if (cur.active && journey && typeof journey.noteGalleryImage === "function")
					journey.noteGalleryImage(cur.file || cur.name);
				window.__soulogGa4Event(
					"gallery_image_view",
					{
						image_group: cur.group,
						image_position: cur.position,
						image_name: cur.name,
						image_file: cur.file,
						trigger: cur.trigger,
					},
					this,
				);
			}, 1000);
		}

		/** soulog-module-tracking.js 点击时来取：当前图 + 最后一次主动选的图（文件名，没有则空串） */
		ga4GalleryContext() {
			const id = (i) => (i ? i.file || i.name : "");
			return { gallery_image: id(this.curImage), gallery_picked: id(this.pickedImage) };
		}

		/**
		 * 缩略图条放不下时横向滚动，把当前那张滚进可见区。
		 * 程序跳转（切色 / 选配件）的目标可能在条外，不滚的话高亮框顾客看不到。
		 * 不用 scrollIntoView：它会连带整页纵向滚动。
		 */
		revealThumb(thumb) {
			const strip = thumb && thumb.parentElement;
			if (!strip || strip.scrollWidth <= strip.clientWidth) return;
			const s = strip.getBoundingClientRect();
			const t = thumb.getBoundingClientRect();
			const left = strip.scrollLeft + (t.left - s.left) - (s.width - t.width) / 2;
			strip.scrollTo({ left, behavior: "smooth" });
		}

		/**
		 * 按配件标题找图廊里对应的图（配件是另一个商品，media id 对不上，只能靠 alt）。
		 * 先整句相等，再整词包含；都没有返回 -1，调用方不动图廊。
		 */
		findSlideByTitle(title, slides = this.slides) {
			const want = String(title || "")
				.trim()
				.toLowerCase();
			if (!want) return -1;
			const alts = slides.map((s) =>
				String(s.dataset.mediaAlt || "")
					.trim()
					.toLowerCase(),
			);
			const exact = alts.indexOf(want);
			if (exact > -1) return exact;
			const escaped = want.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
			const word = new RegExp(`(^|[^a-z0-9])${escaped}($|[^a-z0-9])`);
			return alts.findIndex((a) => word.test(a));
		}

		/* ------------------------------------------------------ 颜色 / 数量 */

		selectColor(el) {
			const id = Number(el.dataset.variantId);
			if (!id) return;
			// 色块带的是 Shopify 按当前选中组合挑的变体，可能是套餐变体 —— 归一到同色的「主机单卖」
			const picked = this.variants.find((v) => v.id === id);
			this.currentVariantId = picked ? this.deviceVariantFor(picked).id : id;

			this.querySelectorAll("[data-ssm-swatch]").forEach((s) => {
				const on = s === el;
				s.classList.toggle("is-active", on);
				s.setAttribute("aria-checked", on ? "true" : "false");
			});
			if (this.colorName) this.colorName.textContent = el.dataset.value || "";

			const variant = this.currentVariant();
			preloadDrawerImage(variant);
			// 主机图：色块上带着该色的主机图地址（颜色映射区块配的），没有就用 variant 主图
			this.renderVariantMedia(el.dataset.hostImg || (variant && variant.featured_image && variant.featured_image.src));
			const addonBefore = this.selectedAddonId;
			this.matchAddonToColor(el);
			// 卡包图已露出且卡包跟着颜色换了：图也换成新卡包的（不抢第一张，第一张给下面的颜色图）
			if (this.optionPins && this.optionPins.addon && this.selectedAddonId !== addonBefore) {
				const card = this.querySelector(`[data-ssm-addon][data-variant-id="${this.selectedAddonId}"]`);
				this.optionPins.addon = card ? this.addonSlide(card) : null;
			}
			// 图廊露出该色的图并放第一张（2026-10-21：颜色图默认隐藏，点了才出现，换色直接替换）
			const mediaId = variant && variant.featured_media && variant.featured_media.id;
			this.showOptionMedia(
				"color",
				(this.allSlides || []).find((s) => !s.dataset.ssmPane && Number(s.dataset.mediaId) === mediaId) || null,
			);
			this.renderTotal();
			this.syncForms();
			this.updateBuyMode();
		}

		/**
		 * 颜色上方的主机图卡跟着颜色换（2026-10-20 改版）。
		 * 图挂 data-ssm-variant-img="<像素宽>"：换图时丢掉 srcset，按这个宽度向 CDN 要一张（已有 width 参数就替换），
		 * 避免回落到原图大小。没有图地址就保持原图，不清空。
		 */
		renderVariantMedia(src) {
			if (!src) return;
			this.querySelectorAll("[data-ssm-variant-img]").forEach((img) => {
				const width = Number(img.dataset.ssmVariantImg) || 1072;
				img.removeAttribute("srcset");
				img.src = /[?&]width=\d+/.test(src)
					? src.replace(/([?&])width=\d+/, `$1width=${width}`)
					: `${src}${src.includes("?") ? "&" : "?"}width=${width}`;
			});
		}

		stepQty(delta) {
			this.qty = Math.max(1, this.qty + delta);
			if (this.qtyInput) this.qtyInput.value = this.qty;
			this.renderTotal();
			this.syncForms();
		}

		toggleAddon(el) {
			const wasOn = this.selectedAddonId === Number(el.dataset.variantId);
			this.setAddon(wasOn ? null : el);
			// 选中时露出这款卡包的图并放第一张（2026-10-21：卡包图默认隐藏）；取消时收起
			this.showOptionMedia("addon", wasOn ? null : this.addonSlide(el));
			this.renderTotal();
			this.syncForms();
			this.updateBuyMode();
		}

		/** 点缩略图：选中对应卡包（与点卡片同一条路），已选中的不取消，缺货的不响应 */
		pickAddonThumb(thumb) {
			const id = Number(thumb.dataset.variantId);
			if (thumb.disabled || id === this.selectedAddonId) return;
			const card = this.querySelector(`[data-ssm-addon][data-variant-id="${id}"]`);
			if (card && !card.disabled) this.toggleAddon(card);
		}

		/** 配件卡对应的商品图（alt 匹配配件标题），没有返回 null */
		addonSlide(card) {
			const own = (this.allSlides || this.slides).filter((s) => !s.dataset.ssmPane);
			const idx = this.findSlideByTitle(card && card.dataset.title, own);
			return idx > -1 ? own[idx] : null;
		}

		/** 选中某张配件卡（null = 不要配件）：同步卡片 aria-pressed 与标签文案，不碰图廊 / Total */
		setAddon(el) {
			this.selectedAddonId = el ? Number(el.dataset.variantId) : null;
			if (this.selectedAddonId)
				preloadDrawerImage((this.addonVariants || []).find((v) => v.id === this.selectedAddonId));
			this.querySelectorAll("[data-ssm-addon], [data-ssm-addon-thumb]").forEach((a) => {
				a.setAttribute("aria-pressed", Number(a.dataset.variantId) === this.selectedAddonId ? "true" : "false");
			});
			if (this.addonName) {
				const suffix = this.addonName.dataset.suffix || "";
				this.addonName.textContent = el
					? [el.dataset.title || "", suffix].filter(Boolean).join(" ")
					: this.addonNoneLabel;
			}
		}

		/**
		 * 选颜色时卡包跟到同色（2026-10-21）：同色套装优先（紫机 + 紫卡包）。
		 * 对应关系配在「颜色映射」区块的「对应卡包」（机身色名和卡包名对不上，不能靠名字猜），色块直出 data-addon-match。
		 * 没配、名字对不上、或那款缺货（卡片 disabled）时保持当前选择；顾客之后仍可手动改选或取消。
		 * 只改选中态，Total / 表单由调用方刷新。
		 */
		matchAddonToColor(swatch) {
			const want = String((swatch && swatch.dataset.addonMatch) || "")
				.trim()
				.toLowerCase();
			if (!want) return;
			const card = Array.from(this.querySelectorAll("[data-ssm-addon]")).find(
				(a) =>
					!a.disabled &&
					String(a.dataset.title || "")
						.trim()
						.toLowerCase() === want,
			);
			if (card) this.setAddon(card);
		}

		/* ------------------------------------------------ 赠品套餐变体 */

		/*
		 * 赠品套餐变体（2026-10-20）：主商品多一个选项（如 Card Holder：Device Only / Free Gray Card Holder /
		 * Free Purple Card Holder），套餐变体与主机同价、SKU 是领星的套餐 SKU。顾客选中赠品卡包时，
		 * 进购物车 / 结账的是「同色 + 名字里含这款卡包标题（Gray / Purple）」的套餐变体，不再另加一行赠品 ——
		 * 这样原生钱包按钮（Apple Pay / Shop Pay，一次只能带一个变体）也能把赠品带进订单。
		 * 界面不变：卡包卡片、划线价 + Free、Total 都照旧，套餐选项本身不在页面上画。
		 * 商品没有套餐变体、或对应套餐缺货时，退回原来「主机 + 赠品行」的做法（addGiftLine）。
		 */

		/** 变体的某个非颜色选项值里有没有这个词（整词、不分大小写） */
		variantMentions(variant, word) {
			const want = String(word || "")
				.trim()
				.toLowerCase();
			if (!variant || !want || want === "default title") return false;
			const escaped = want.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
			const re = new RegExp(`(^|[^a-z0-9])${escaped}($|[^a-z0-9])`);
			return (variant.options || []).some(
				(value, i) => i !== this.colorIndex && re.test(String(value || "").toLowerCase()),
			);
		}

		sameColor(a, b) {
			if (this.colorIndex < 0 || !a || !b) return true;
			return (a.options || [])[this.colorIndex] === (b.options || [])[this.colorIndex];
		}

		/** 是不是套餐变体：非颜色选项里提到了任何一款卡包 */
		isBundleVariant(variant) {
			return (this.addonVariants || []).some((a) => this.variantMentions(variant, a.title));
		}

		/** 与给定变体同色的「主机单卖」变体；商品没有套餐变体时就是它自己 */
		deviceVariantFor(variant) {
			if (!variant) return {};
			if (!this.isBundleVariant(variant)) return variant;
			return (this.variants || []).find((v) => this.sameColor(v, variant) && !this.isBundleVariant(v)) || variant;
		}

		/** 当前颜色 + 选中赠品对应的、有货的套餐变体；不适用或缺货返回 null */
		activeBundle() {
			if (!this.addonIsGift || !this.selectedAddonId || !this.variants || !this.addonVariants) return null;
			const addon = this.addonVariants.find((a) => a.id === this.selectedAddonId);
			const base = this.currentVariant();
			if (!addon || !base) return null;
			const bundle = this.variants.find((v) => this.sameColor(v, base) && this.variantMentions(v, addon.title));
			return bundle && bundle.available !== false ? bundle : null;
		}

		/** 这一单真正要买的主商品变体：有套餐就是套餐，否则是主机单卖 */
		checkoutVariantId() {
			const bundle = this.activeBundle();
			return bundle ? bundle.id : this.currentVariantId;
		}

		/* ------------------------------------------------ 原生结账表单同步 */

		/** 把这一单的 variant（含套餐）/ 数量写进动态结账按钮与分期说明所在的原生表单 */
		syncForms() {
			const id = this.checkoutVariantId();
			this.formVariantInputs.forEach((input) => {
				input.value = id;
			});
			this.formQtyInputs.forEach((input) => {
				input.value = this.qty;
			});
		}

		/**
		 * 原生动态结账按钮（Apple Pay / Shop Pay / Buy it now）一次只能带一个变体：
		 * 没选配件、或选的赠品有对应套餐变体 → 用它（2026-10-20 起替换 Buy Now，顾客一键付款）；
		 * 选了付费配件、或赠品没有套餐变体 / 缺货 → 藏起来，亮自定义 Buy Now（加购 → 购物车页，能带上配件）。
		 */
		updateBuyMode() {
			this.renderAvailability();
			if (!this.hasDynamic) return;
			const needsCartRoute = !!this.selectedAddonId && !this.activeBundle();
			// 缺货色：原生动态按钮（Shopify 渲染，文案改不了）藏起来，换上显示 Sold out 的禁用按钮
			const useOwnButton = needsCartRoute || !this.isAvailable();
			if (this.dynamicForm) this.dynamicForm.classList.toggle("is-hidden", useOwnButton);
			if (this.buyButton) this.buyButton.classList.toggle("is-hidden", !useOwnButton);
		}

		/**
		 * 这一单能不能买：按选中颜色的变体（有可买的套餐就看套餐）判，不是整个商品（2026-10-22：选了缺货色
		 * 按钮还是 Buy Now，点下去才报错）。Liquid 只按 p.available 渲染首屏，换色之后全靠这里。
		 */
		isAvailable() {
			// 还没读到变体数据（区块未初始化）时不拦，交给加购接口判
			if (!Array.isArray(this.variants) || !this.variants.length) return true;
			const variant = this.activeBundle() || this.currentVariant();
			return !variant || variant.available !== false;
		}

		/** 模块按钮 + 桌面悬浮栏按钮：缺货时禁用并改成 Sold out；加入购物车只禁用不改字 */
		renderAvailability() {
			const ok = this.isAvailable();
			if (this.classList) this.classList.toggle("is-sold-out", !ok);
			[this.buyButton, this.floatBuy].forEach((b) => {
				if (!b || (b.getAttribute && b.getAttribute("aria-busy") === "true")) return;
				b.disabled = !ok;
				const label = b.dataset && b.dataset.ssmLabel;
				if (label) b.textContent = ok ? label : this.soldOutLabel;
			});
			this.querySelectorAll("[data-ssm-add-to-cart]").forEach((b) => {
				if (!(b.getAttribute && b.getAttribute("aria-busy") === "true")) b.disabled = !ok;
			});
		}

		/* ---------------------------------------------------------- 合计 */

		currentVariant() {
			return this.variants.find((v) => v.id === this.currentVariantId) || this.variants[0];
		}

		/** 这一单的原价合计与折后合计（分）。页面合计与 GA4 add_to_cart 的 value 共用这一份算法。 */
		orderTotals() {
			const variant = this.activeBundle() || this.currentVariant();
			if (!variant) return null;
			let cents = variant.price * this.qty;
			if (this.selectedAddonId && !this.addonIsGift) {
				const a = this.addonVariants.find((v) => v.id === this.selectedAddonId);
				if (a) cents += a.price;
			}
			// 折扣额按分向下取整，与 Shopify 结账实测一致（x.99 的一成舍去不足 1 分的部分）
			const discounted = this.discountPercent > 0 && cents >= this.discountMinCents;
			const finalCents = discounted ? cents - Math.floor((cents * this.discountPercent) / 100) : cents;
			return { cents, discounted, finalCents };
		}

		renderTotal() {
			if (!this.totalNode) return;
			const totals = this.orderTotals();
			if (!totals) return;
			const { cents, discounted, finalCents } = totals;
			this.totalNode.textContent = this.formatMoney(finalCents);
			if (this.totalCompareNode) {
				this.totalCompareNode.textContent = this.formatMoney(cents);
				this.totalCompareNode.hidden = !discounted;
			}
			this.renderUnitPrice();
			// 购买浮条（soulog-buy-pill.js）跟着 Total 改价：它的按钮买的就是这一单（buyNow），价格得是同一个数
			this.dispatchEvent(new CustomEvent("ssm:total", { bubbles: true, detail: totals }));
		}

		/** 单价行：当前颜色一台，不计数量与配件；折扣门槛按单价判（与 Liquid 直出一致） */
		renderUnitPrice() {
			const variant = this.currentVariant();
			if (!this.priceNode || !variant) return;
			const cents = variant.price;
			const discounted = this.discountPercent > 0 && cents >= this.discountMinCents;
			const finalCents = discounted ? cents - Math.floor((cents * this.discountPercent) / 100) : cents;
			this.priceNode.textContent = this.formatMoney(finalCents);
			if (this.priceCompareNode) {
				this.priceCompareNode.textContent = this.formatMoney(cents);
				this.priceCompareNode.hidden = !discounted;
			}
		}

		/**
		 * 用 shop.money_format 的模板串格式化，不自己拼 "$"。
		 * 支持 {{amount}} / {{amount_no_decimals}} / {{amount_with_comma_separator}} 四种占位符，
		 * 覆盖 Shopify 的常见格式；认不出来时退回两位小数，至少不会显示成 NaN。
		 */
		formatMoney(cents) {
			const value = cents / 100;
			const withCommas = (n, decimals) => {
				const fixed = n.toFixed(decimals);
				const [int, dec] = fixed.split(".");
				const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
				return dec ? `${grouped}.${dec}` : grouped;
			};
			const withDots = (n, decimals) => withCommas(n, decimals).replace(/,/g, " ").replace(/\./g, ",").replace(/ /g, ".");

			const replaced = this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
				switch (token) {
					case "amount":
						return withCommas(value, 2);
					case "amount_no_decimals":
						return withCommas(value, 0);
					case "amount_with_comma_separator":
						return withDots(value, 2);
					case "amount_no_decimals_with_comma_separator":
						return withDots(value, 0);
					default:
						return withCommas(value, 2);
				}
			});
			return replaced === this.moneyFormat ? `$${withCommas(value, 2)}` : replaced;
		}

		/* ---------------------------------------------------------- 加购 */

		showError(message) {
			if (!this.errorNode) return;
			this.errorNode.textContent = message || "";
			this.errorNode.hidden = !message;
		}

		/** 这一单的 line items：套餐变体（每台主机一个卡包）；没有套餐时 = 主商品 + 选中的配件 */
		orderItems() {
			const bundle = this.activeBundle();
			if (bundle) return [{ id: bundle.id, quantity: this.qty }];
			const items = [{ id: this.currentVariantId, quantity: this.qty }];
			if (this.selectedAddonId) items.push({ id: this.selectedAddonId, quantity: 1 });
			return items;
		}

		async submit(goToCheckout) {
			if (!this.isAvailable()) return;
			if (goToCheckout) return this.buyNow();
			const ok = await this.addOrderToCart(this.querySelectorAll("[data-ssm-buy], [data-ssm-add-to-cart]"));
			if (ok) this.openCart();
		}

		/**
		 * 把这一单加进顾客购物车（Add to Cart 与立即购买共用）。成功返回 true。
		 *
		 * 赠品模式下卡包不进 /cart/add.js（2026-10-12 线上实测）：add.js 每一行都会触发一次 Shopify
		 * product_added_to_cart，Google 渠道据此各发一条 Ads 加购转化 + GA4 add_to_cart，
		 * 点一次 Add to Cart 记两次加购。卡包另走 addGiftLine（不触发加购事件）。
		 * 付费配件是真加购，仍和主商品一起走 add.js。
		 */
		async addOrderToCart(buttons, { withDrawer = false } = {}) {
			const items = this.orderItems();
			this.pendingSections = null;
			// 有套餐变体时卡包已经在套餐里，一行 add.js 即可（只触发一次加购事件）
			if (!this.addonIsGift || !this.selectedAddonId || this.activeBundle())
				return this.postItems(items, buttons, withDrawer);
			// 购物车本来就有东西（token 已知、购物车一定存在）：赠品先加，主机最后加并顺带拿回抽屉 HTML，
			// 省掉读 /cart.js 和单独拉抽屉两个来回（2026-10-29 提速）。加购事件仍只由主机这次 add.js 触发，口径不变。
			// 赠品没加上（被拒 / 接口不可用）就退回老顺序：主机 → addGiftLine（它自带回落与报错）
			if (this.knownCartToken) {
				setBusy(buttons, true);
				const gift = await this.addLineViaStorefront(this.knownCartToken, this.selectedAddonId).catch(() => null);
				if (gift === true) return this.postItems(items.slice(0, 1), buttons, withDrawer);
			}
			if (!(await this.postItems(items.slice(0, 1), buttons))) return false;
			return this.addGiftLine(this.selectedAddonId, buttons);
		}

		/**
		 * 把赠品加 1 件到顾客当前购物车，且不触发加购事件。
		 *
		 * 走 Storefront API cartLinesAdd（cart id = gid://shopify/Cart/<cart.js 的 token>）。2026-10-13 线上实测
		 * （tmp/atc-probe）：cartLinesAdd 加进来的行只进 Shopify 自己的统计，不转给渠道像素，Ads / GA4 都不发；
		 * 而 10-12 用的 /cart/update.js 照样触发 product_added_to_cart —— 那次修复其实没去掉重复加购。
		 * cartLinesAdd 是「加 N 件」，与 add.js 同语义，不用先算已有几件。
		 *
		 * API 不可用（HTTP / GraphQL 错误）时回落 update.js：宁可多记一次加购，也不能让顾客丢赠品。
		 * 失败时主商品已经加上了：提示顾客去看购物车，不自动重试（可能其实已加上）。
		 */
		async addGiftLine(id, buttons) {
			const routes = (window.theme && window.theme.routes) || {};
			setBusy(buttons, true);
			try {
				const cart = await this.readCart();
				if (cart && cart.token) {
					const viaApi = await this.addLineViaStorefront(cart.token, id).catch(() => null);
					if (viaApi === true) return true;
					if (viaApi === null) {
						const have = (cart.items || [])
							.filter((line) => line.variant_id === id)
							.reduce((n, line) => n + line.quantity, 0);
						const res = await fetchJSON(
							`${routes.cart_update_url || "/cart/update"}.js`,
							{
								method: "POST",
								headers: { "Content-Type": "application/json", Accept: "application/json" },
								body: JSON.stringify({ updates: { [id]: have + 1 } }),
							},
							WRITE_TIMEOUT_MS,
						);
						if (res.ok) return true;
					}
				}
			} catch {
				/* 落到下面统一提示 */
			} finally {
				setBusy(buttons, false);
			}
			document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
			this.showError(MESSAGES.giftFailed);
			return false;
		}

		/**
		 * cartLinesAdd 往顾客当前购物车加 1 件。true = 加上了；false = Shopify 明确拒绝（userErrors，如缺货）；
		 * 抛错 = API 不可用 / 网络失败，由调用方决定是否回落。
		 */
		async addLineViaStorefront(token, id) {
			const response = await fetchJSON(
				`/api/${STOREFRONT_API_VERSION}/graphql.json`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json", Accept: "application/json" },
					body: JSON.stringify({
						query: `mutation SoulogGift($cartId: ID!, $lines: [CartLineInput!]!) {
						cartLinesAdd(cartId: $cartId, lines: $lines) { cart { id } userErrors { message } }
					}`,
						variables: {
							cartId: `gid://shopify/Cart/${token}`,
							lines: [{ merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity: 1 }],
						},
					}),
				},
				WRITE_TIMEOUT_MS,
			);
			const data = response.data;
			if (!data || !response.ok || (data.errors && data.errors.length) || !data.data || !data.data.cartLinesAdd) {
				throw new Error("storefront-api-unavailable");
			}
			const { cart, userErrors } = data.data.cartLinesAdd;
			if (userErrors && userErrors.length) return false;
			return Boolean(cart);
		}

		/**
		 * 立即购买：加进顾客购物车，再跳购物车页（2026-10-20 需求，仿 Plaud：Buy Now 先到购物车页，
		 * 顾客在那里用 Check Out 或钱包按钮付款）。
		 *
		 * 此前（2026-09-22 ~ 10-20）是直跳结账，购物车已有东西时还用 Storefront API cartCreate 建独立购物车、
		 * 只结算这一单。改成跳购物车页后顾客原有商品本来就会一起出现，独立购物车没有意义，已删除。
		 * 附带好处：统一走 /cart/add.js，Shopify 会把 product_added_to_cart 转给渠道像素，
		 * Google Ads / Meta / GA4 的加购都由「Google & YouTube」等渠道插件发，主题不再补发（补发会双计）。
		 *
		 * 购买路径摘要与 GA4 用户 ID 在加购后写进购物车属性：顾客在购物车页点钱包按钮会绕过 Check Out 表单，
		 * 那条路没有提交时注入（assets/soulog-module-tracking.js），订单的 _soulog_journey 只能靠这里写的这份。
		 *
		 * trigger：模块外的触发元素（购买浮条按钮，2026-10-12），和模块里的按钮一起进忙碌态。
		 * 返回 true = 正在跳购物车页；false = 没加上，错误已显示在模块里。
		 */
		async buyNow(trigger) {
			// 购买浮条等模块外入口也走这里：缺货色直接不买，由调用方滚回模块让顾客换色
			if (!this.isAvailable()) return false;
			const buttons = [...this.querySelectorAll("[data-ssm-buy], [data-ssm-add-to-cart]")];
			if (trigger) buttons.push(trigger);
			// 手机端点下去立刻弹抽屉，加购在背后跑，好了再换内容（2026-10-29，仿 heypocket）：
			// 抽屉里原本有商品就先显示上一次的样子（暂不可点），原本是空的就显示骨架屏
			const drawer = this.buyNowDrawer();
			// 弹抽屉这条路按钮不变灰（2026-10-29 用户：抽屉出来了按钮还是灰的，像点不了）：进度由抽屉自己表达，
			// 防连点改用内部标记 —— 加购还没回来时再点（顾客先关了抽屉）只把抽屉重新打开，不再发一单
			if (drawer && this.drawerBuying) {
				drawer.open();
				return true;
			}
			if (!drawer) setBusy(buttons, true);
			if (drawer) {
				this.drawerBuying = true;
				installDrawerGuard();
				const stale = Boolean(drawer.querySelector(".product-cart-item"));
				drawer.classList.add(stale ? DRAWER_STALE_CLASS : DRAWER_LOADING_CLASS);
				// 先画后对账：把抽屉里本模块那一行直接改成这次选的颜色 / 数量（只改显示，见 paintDrawerPrediction）
				// 预测只是显示，出任何错都不能挡住购买
				if (stale) {
					try {
						this.paintDrawerPrediction(drawer);
					} catch {
						/* 照旧显示上一次的内容 */
					}
				}
				drawer.open();
			}
			// 自己加购会派发 cart:refresh，跳页前别再白发一次预读（见 prefetchCart）
			this.leaving = true;
			let ok = false;
			try {
				await this.clearPreviousOrder();
				// 抽屉这条路不给按钮传进去：postItems / addGiftLine 里的 setBusy 一样会把按钮变灰
				ok = await this.addOrderToCart(drawer ? [] : buttons, { withDrawer: Boolean(drawer) });
			} finally {
				// 没加上（含抛错）立刻放行，顾客要能马上重试；加上了等抽屉内容换好再放行（见下方）
				if (drawer && !ok) this.drawerBuying = false;
			}
			if (!ok) {
				this.leaving = false;
				// 没加上：收起抽屉，错误提示在购物模块里（调用方会滚回模块）；抽屉可能被「先画」改过，按服务端重画回真实购物车
				if (drawer) {
					cancelDrawerCheckout(drawer);
					closeDrawer(drawer);
					document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
				}
				return false;
			}
			// 不等返回：keepalive 保证跳页后照样发完（2026-10-29 提速，省一个请求）
			this.writeJourneyToCart();
			if (drawer) {
				try {
					await this.fillDrawerAfterBuyNow(drawer);
				} finally {
					this.drawerBuying = false;
				}
				this.leaving = false;
				return true;
			}
			// 按钮保持禁用：页面马上要离开
			setBusy(buttons, true);
			// 不用等像素：10-13 跳结账时 Meta 要等 1s 才收得到加购，10-20 改跳购物车页后
			// 10-29 实测直接跳 AddToCart 每次都发出（200），1s 等待已删
			const routes = (window.theme && window.theme.routes) || {};
			window.location.href = routes.cart_url || "/cart";
			return true;
		}

		/**
		 * 先画后对账（2026-10-29）：点下去就把抽屉里本模块那一行改成这次选的颜色 / 数量 / 赠品颜色，
		 * 顾客换了色不用等几秒才看到新颜色。只改显示：
		 *   · 只改确定的东西 —— 图、规格文字、数量；价格类数字不预测，变灰表示「更新中」（.soulog-drawer-predicting），
		 *     服务端结果回来整块换成真实渲染，数字再从旧值滚到新值；
		 *   · 期间整块不可点（stale 态），顾客没法在预测画面上结账；
		 *   · 加购失败时抽屉收起并按服务端重画，预测画面不会留下。
		 * 套餐变体模式结构不同，不预测，照旧显示上一次的内容。
		 */
		paintDrawerPrediction(drawer) {
			if (this.activeBundle()) return;
			const main = this.currentVariant();
			const addon = this.selectedAddonId ? (this.addonVariants || []).find((v) => v.id === this.selectedAddonId) : null;
			const mainIds = new Set((this.variants || []).map((v) => v.id));
			const addonIds = new Set((this.addonVariants || []).map((v) => v.id));
			let mainDone = false;
			let addonDone = false;
			drawer.classList.add(DRAWER_PREDICT_CLASS);
			drawer.querySelectorAll(".product-cart-item[data-variant-id]").forEach((row) => {
				const id = Number(row.getAttribute("data-variant-id"));
				if (mainIds.has(id)) {
					// 本模块的主机：第一行改成这次的，其余（之前别的颜色）会被清掉，先藏起来
					if (mainDone || !main) row.style.display = "none";
					else paintDrawerRow(row, main, this.qty);
					mainDone = true;
				} else if (addonIds.has(id)) {
					if (addonDone || !addon) row.style.display = "none";
					else paintDrawerRow(row, addon, 1);
					addonDone = true;
				}
			});
		}

		/**
		 * 手机端（<768，与全站主断点一致）立即购买弹购物车抽屉，不再跳购物车页（2026-10-29）。电脑端仍跳页。
		 * 返回主题抽屉元素 = 走弹抽屉；null = 不是手机 / 后台关了抽屉，照旧跳购物车页。
		 */
		buyNowDrawer() {
			const drawer = document.getElementById("Cart-Drawer");
			if (!drawer || typeof drawer.open !== "function") return null;
			if (!window.matchMedia || !window.matchMedia("(max-width: 767px)").matches) return null;
			return drawer;
		}

		/**
		 * 加购成功后把抽屉换成最新内容、撤掉转圈。
		 *
		 * 抽屉是点下去就开的（转圈态），这里拿 Section Rendering 的最新 HTML 换进去。不用主题的 cart:refresh：
		 * 它没有完成回调，转圈不知道何时撤，下面的 view_cart 也要读加购之后那份标记。
		 * 拉不到就撤掉转圈、交给 cart:refresh 异步刷新，这次不报 view_cart（宁缺勿错：那时抽屉里还是旧购物车）。
		 *
		 * view_cart：原来手机 Buy Now 落到购物车页时由页面加载报一条；改弹抽屉后这条要补上，
		 * 否则「进购物车」那一步从上线当天起断档（2026-10-29 用户：数据要和现在保持一样）。
		 * 由 layout/theme.liquid 监听 soulog:buy-now-drawer 报，解析逻辑与另外两个触发点共用一份。
		 * 顾客在转圈时就把抽屉关了：内容照换（下次打开是对的），但不报 —— 他没看到购物车。
		 */
		async fillDrawerAfterBuyNow(drawer) {
			let fresh = false;
			// 换内容前记下旧数字，换完让变了的数字从旧值滚到新值
			const before = readDrawerNumbers(drawer);
			// 加购请求已顺带带回抽屉 HTML（见 postItems）就直接用，不再单独拉
			const carried = this.pendingSections;
			this.pendingSections = null;
			if (carried) fresh = renderDrawerSections(carried);
			if (!fresh) {
				try {
					// 不能带 Accept: application/json：商品页 URL 带了它，Shopify 会无视 ?sections= 直接回商品 JSON
					// （2026-10-29 实测）。主题 CartDrawer.refresh() 也是不带头的同一个请求
					const res = await fetchJSON(`${location.pathname}?sections=cart-drawer,cart-bubble`, {}, READ_TIMEOUT_MS);
					if (res.ok && res.data) fresh = renderDrawerSections(res.data);
				} catch {
					/* 下面兜底 */
				}
			}
			drawer.classList.remove(DRAWER_LOADING_CLASS, DRAWER_STALE_CLASS, DRAWER_PREDICT_CLASS);
			// 更新中被点过的 Checkout 现在提交（真实购物车已加完）
			resumeDrawerCheckout(drawer);
			if (fresh) rollDrawerNumbers(drawer, before);
			if (!fresh) {
				document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
				return;
			}
			if (drawer.classList.contains("active")) {
				document.documentElement.dispatchEvent(new CustomEvent("soulog:buy-now-drawer", { bubbles: true }));
			}
		}

		/**
		 * 立即购买前，把购物车里本模块的商品（主机各色 / 套餐 / 卡包各色）清零（2026-10-28 需求）。
		 * Buy Now 去购物车页后，顾客选错返回重选再点，add.js 会在上一次的基础上叠加，购物车出现两份。
		 * 页面只有 Buy Now 一个入口（Add to Cart 文案留空不显示；单买卡包按需求不考虑），所以按商品清，不打标记。
		 * 只清本模块 variant，别的商品不动。读 / 写失败都不拦购买 —— 最坏就是回到改前的叠加。
		 */
		async clearPreviousOrder() {
			const ids = new Set(
				[...(this.variants || []), ...(this.addonVariants || [])]
					.map((v) => v.id)
					.concat(this.orderItems().map((item) => item.id)),
			);
			// 用预读好的（见 prefetchCart），没有才现读；用一次就作废，失败重试时读最新的
			const snapshot = this.cartSnapshot;
			this.cartSnapshot = null;
			const cart = await (snapshot || this.readCart());
			// 记下购物车 token：购物车里本来就有东西（一定已建好）时，赠品可以直接用它加，省一次 /cart.js（见 addOrderToCart）
			this.knownCartToken = cart && cart.token && cart.item_count > 0 ? cart.token : null;
			if (!cart || !cart.items) return;
			const updates = {};
			cart.items.forEach((line) => {
				if (ids.has(line.variant_id)) updates[line.variant_id] = 0;
			});
			if (!Object.keys(updates).length) return;
			const routes = (window.theme && window.theme.routes) || {};
			try {
				await fetchJSON(
					`${routes.cart_update_url || "/cart/update"}.js`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json", Accept: "application/json" },
						body: JSON.stringify({ updates }),
					},
					WRITE_TIMEOUT_MS,
				);
			} catch {
				/* 不拦购买 */
			}
		}

		/**
		 * 预读购物车，留给 Buy Now 的 clearPreviousOrder 用，点击时省掉一次 /cart.js（2026-10-29 提速）。
		 * 正在跳购物车页时不读（自己的加购会派发 cart:refresh，那时再读是白发请求）。
		 * 快照可能旧（如别的标签页改了没切回来）：最坏是购物车里多出一份，与读失败时的兜底一样。
		 */
		prefetchCart() {
			if (this.leaving) return;
			this.cartSnapshot = this.readCart();
		}

		/** 读顾客当前购物车（/cart.js）。读不到返回 null，不拦下单。 */
		async readCart() {
			const routes = (window.theme && window.theme.routes) || {};
			try {
				const res = await fetchJSON(
					`${routes.cart_url || "/cart"}.js`,
					{ headers: { Accept: "application/json" } },
					READ_TIMEOUT_MS,
				);
				return res.ok && res.data ? res.data : null;
			} catch {
				return null;
			}
		}

		/**
		 * 立即购买把购买路径摘要与 GA4 用户 ID 写到购物车属性上，
		 * 否则顾客在购物车页走钱包按钮时订单没有 _soulog_journey。广告归因 _soulog_attr_* 与折扣码本来就在这个购物车上，不用动。
		 * 写失败不拦结账，只是这一单少了路径摘要。
		 */
		async writeJourneyToCart() {
			const journeyApi = window.__soulogJourney;
			if (!journeyApi) return;
			const attributes = {};
			// 没同意统计时两者都返回空串：写空串等于清掉残留的旧值
			attributes[journeyApi.attr] = journeyApi.snapshot(this.buyButton) || "";
			if (journeyApi.gaAttr) attributes[journeyApi.gaAttr] = journeyApi.gaCid() || "";
			const routes = (window.theme && window.theme.routes) || {};
			try {
				await fetchJSON(
					`${routes.cart_update_url || "/cart/update"}.js`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json", Accept: "application/json" },
						body: JSON.stringify({ attributes }),
						keepalive: true,
					},
					WRITE_TIMEOUT_MS,
				);
			} catch {
				/* 不拦结账 */
			}
		}

		/**
		 * 只把这一个配件加入购物车（2026-09-22 需求：卡包可以不带硬件单独买）。
		 * 与卡片的选中状态无关 —— 不改 selectedAddonId，也不影响 Total。
		 */
		async addAddonOnly(el) {
			const id = Number(el.dataset.variantId);
			if (!id) return;
			const ok = await this.postItems([{ id, quantity: 1 }], [el]);
			if (ok) this.openCart();
		}

		/**
		 * POST /cart/add.js；请求期间禁用传入的按钮。成功返回 true，失败显示原因并返回 false。
		 *
		 * 三类失败分开提示：
		 *   · Shopify 业务错误（售罄 / 超量 422 + description）—— 原样显示，顾客能据此改数量；
		 *   · 服务器异常（5xx、非 JSON 错误页）—— 通用「稍后再试」，不把 "Unexpected token <" 甩给顾客；
		 *   · 网络层失败 / 超时 —— 请求可能**已经到了服务器、只是响应没回来**，商品也许已加上。
		 *     此时不自动重试（会重复加购），而是让抽屉 / 角标按服务端真实状态刷新一次，并提示顾客先看购物车。
		 */
		async postItems(items, busyButtons, withDrawer = false) {
			this.showError("");
			const routes = (window.theme && window.theme.routes) || {};
			const addUrl = routes.cart_add_url || "/cart/add";

			setBusy(busyButtons, true);
			let response;
			try {
				response = await fetchJSON(
					`${addUrl}.js`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json", Accept: "application/json" },
						// 手机立即购买：同一个请求顺带拿回加购后的抽屉 / 角标 HTML（Section Rendering），省一个来回
						body: JSON.stringify(
							withDrawer ? { items, sections: "cart-drawer,cart-bubble", sections_url: location.pathname } : { items },
						),
					},
					WRITE_TIMEOUT_MS,
				);
			} catch (error) {
				document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
				this.showError(error && error.name === "TimeoutError" ? MESSAGES.addUnconfirmed : MESSAGES.network);
				return false;
			} finally {
				setBusy(busyButtons, false);
			}

			if (response.ok) {
				if (withDrawer && response.data && response.data.sections) this.pendingSections = response.data.sections;
				return true;
			}
			const data = response.data;
			const shopifyMessage = data && (data.description || data.message);
			this.showError(response.status >= 500 || !shopifyMessage ? MESSAGES.server : shopifyMessage);
			return false;
		}

		/**
		 * 交给主题刷新购物车并打开抽屉，不自己画 UI。
		 * 必须直接调主题抽屉的 open()：`cart-drawer:open` 是 open() **之后**才发的通知
		 * （theme.liquid 靠它报 view_cart），派发它并不会打开抽屉 —— 2026-09-27 前加购只 +1 角标。
		 * cart:refresh 让抽屉异步重拉内容，先打开、内容随后刷新，与主题 product.js 的体验一致。
		 * 后台关了抽屉（#Cart-Drawer 不存在）时跳购物车页，别让用户以为没加上。
		 */
		openCart() {
			document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
			const drawer = document.getElementById("Cart-Drawer");
			if (drawer && typeof drawer.open === "function") {
				drawer.open();
				return;
			}
			const routes = (window.theme && window.theme.routes) || {};
			window.location.href = routes.cart_url || "/cart";
		}
	}

	customElements.define("soulog-shop-module", SoulogShopModule);
}

/*
 * 购物模块底部跳转导航（sections/soulog-shop-module.liquid 的 data-ssm-jump-nav，2026-10-21）。
 *
 *   · 本页找得到锚点：平滑滚过去，减掉吸顶页头高度（--header-height），落地后再校正一次
 *     —— 中间区块懒加载 / 滚动视频撑高会让第一次算的位置偏掉；
 *   · 本页找不到（首页的购物模块）：改跳商品页同一锚点（data-ssm-jump-fallback）；
 *     已经在商品页还找不到 = 对应区块被停用，把这一项藏掉；
 *   · 非 # 地址（Q&A）是普通 target=_blank 链接，这里不管。
 * 本文件在页面上可能被加载两次（首页 + 商品页两个模块），靠 window 标记只挂一次。
 * 单测用 vm + 极简 document 桩加载本文件，桩里没有 querySelectorAll，故一并判断。
 */
if (!window.__soulogShopJumpNav && typeof document.querySelectorAll === "function") {
	window.__soulogShopJumpNav = true;

	const headerOffset = () =>
		parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;

	const targetTop = (el) => el.getBoundingClientRect().top + window.scrollY - headerOffset();

	const jumpTo = (el) => {
		const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		window.scrollTo({ top: targetTop(el), behavior: smooth ? "smooth" : "auto" });
		let settled = false;
		const correct = () => {
			if (settled) return;
			settled = true;
			window.removeEventListener("scrollend", correct);
			const top = targetTop(el);
			if (Math.abs(window.scrollY - top) > 2) window.scrollTo({ top, behavior: "auto" });
		};
		window.addEventListener("scrollend", correct);
		setTimeout(correct, 1500);
	};

	const idOf = (link) => decodeURIComponent((link.getAttribute("href") || "").slice(1));

	const prepare = () => {
		document.querySelectorAll("[data-ssm-jump]").forEach((link) => {
			const id = idOf(link);
			if (id && document.getElementById(id)) return;
			const fallback = link.getAttribute("data-ssm-jump-fallback") || "";
			let samePage = true;
			try {
				samePage = new URL(fallback, location.href).pathname === location.pathname;
			} catch (e) {
				/* 地址解析不了就当同页处理：藏掉，不跳坏链接 */
			}
			if (id && fallback && !samePage) {
				link.setAttribute("href", fallback);
				link.removeAttribute("data-ssm-jump");
			} else {
				const item = link.closest(".ssm__jump-item");
				if (item) item.hidden = true;
			}
		});
	};

	if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", prepare);
	else prepare();

	document.addEventListener("click", (event) => {
		const link = event.target.closest && event.target.closest("[data-ssm-jump]");
		if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
		const el = document.getElementById(idOf(link));
		if (!el) return;
		event.preventDefault();
		jumpTo(el);
		// 点完不要停在黑色（2026-10-21 用户）：鼠标点击后链接保留焦点，部分浏览器会一直套 :focus-visible 的黑色
		if (event.detail > 0 && link.blur) link.blur();
		if (history.replaceState) history.replaceState(null, "", "#" + el.id);
	});
}

/*
 * 桌面悬浮购买栏（sections/soulog-shop-module.liquid 的 data-ssm-float，Figma 1608:9193，2026-10-21）。
 * 设计师：「不用过去，做隐藏消失」—— 不跟着滚，单独一条固定在屏幕底部：
 *   · 显示：右栏（.ssm__panel）顶部已越过屏幕一半（同底部购买浮条收起的条件），且右栏自己的购买按钮还没完整进屏；
 *   · 消失：右栏的 Total 行露出三分之二就淡出，或右栏离开屏幕。
 * 只在桌面两栏显示（CSS 控制），手机 / 平板仍是 .ssm__bar 自己 sticky 吸底。
 * 按钮不复制购买逻辑：调模块的 buyNow(按钮)，与顶部促销条、底部购买浮条同一条路（加购含配件 → 购物车页）。
 * 埋点：按钮带 data-ga4-button-key="buy_float"，由三期自动采集 module_click（与模块里的 buy_now 分开）；
 * 第一次真正显示时报 buy_float_view（悬浮栏标了 manual，参数 / 去重 / 推送都在 layout/theme.liquid）。
 * Total 跟着模块的 ssm:total 事件改（换颜色 / 数量 / 配件）。
 * 页面上可能加载两次，靠 window 标记只挂一次；单测桩里没有 IntersectionObserver，一并判断。
 */
if (
	!window.__soulogShopFloat &&
	typeof window.IntersectionObserver === "function" &&
	typeof document.querySelectorAll === "function"
) {
	window.__soulogShopFloat = true;

	const setupFloat = (shop) => {
		const float = shop.querySelector("[data-ssm-float]");
		const bar = shop.querySelector(".ssm__bar");
		const panel = shop.querySelector(".ssm__panel");
		if (!float || !bar || !panel) return;

		let panelIn = false;
		let barBelow = false;
		let viewReported = false;
		const sync = () => {
			const show = panelIn && barBelow;
			float.classList.toggle("is-visible", show);
			float.inert = !show;
			float.setAttribute("aria-hidden", show ? "false" : "true");
			document.body.classList.toggle("has-ssm-float", show);
			if (show && !viewReported && typeof window.__soulogGa4View === "function") {
				viewReported = true;
				window.__soulogGa4View(float);
			}
		};

		// 「右栏在屏」与底部购买浮条（soulog-buy-pill.js）收起的条件对齐：顶部越过屏幕一半才算 ——
		// 原先露 1px 就算，首页购物模块刚从底部冒头时两条购物条同时出现（2026-10-21 用户）
		new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					panelIn = entry.isIntersecting;
				});
				sync();
			},
			{ rootMargin: "0px 0px -50% 0px" },
		).observe(panel);
		// 交接点：右栏的 Total 行露出三分之二就隐藏悬浮条（2026-10-21 用户；此前「整条一露头」太早、「按钮完整进屏」太晚）。
		// 只在它位于屏幕下方（还没滚到）时才显示，滚过去之后不再出。
		const target = bar.querySelector(".ssm__total") || bar;
		const HANDOFF = 2 / 3;
		new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const bottom = entry.rootBounds ? entry.rootBounds.bottom : window.innerHeight;
					barBelow = entry.intersectionRatio < HANDOFF && entry.boundingClientRect.bottom > bottom - 1;
				});
				sync();
			},
			{ threshold: [0, HANDOFF, 1] },
		).observe(target);

		const totalNode = float.querySelector("[data-ssm-float-total]");
		const compareNode = float.querySelector("[data-ssm-float-compare]");
		shop.addEventListener("ssm:total", (event) => {
			const totals = event.detail;
			if (!totals || typeof shop.formatMoney !== "function") return;
			if (totalNode) totalNode.textContent = shop.formatMoney(totals.finalCents);
			if (compareNode) {
				compareNode.textContent = shop.formatMoney(totals.cents);
				compareNode.hidden = !totals.discounted;
			}
		});

		float.addEventListener("click", (event) => {
			const button = event.target.closest && event.target.closest("[data-ssm-float-buy]");
			if (!button || button.disabled || button.getAttribute("aria-busy") === "true") return;
			if (typeof shop.buyNow !== "function") return;
			shop.buyNow(button).then((ok) => {
				// 没加上：错误显示在模块里，滚过去让顾客看到
				if (!ok) bar.scrollIntoView({ behavior: "smooth", block: "center" });
			});
		});
	};

	const setupAll = () => document.querySelectorAll("soulog-shop-module").forEach(setupFloat);
	if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", setupAll);
	else setupAll();
}
